// components/find-investors/InvestorsTable.jsx
import { Fragment, lazy, useMemo, memo, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CountryFlag from '../../countryFlags/countryFlag';
import InvestorLogo from '../../data/InvestorLogo';
import { ExternalLink, Handshake } from 'lucide-react';
import InvestorAccessIcons from '../../investor-display/InvestorAccessIcons';
import InvestorIndustries from '../../investor-display/InvestorIndustries';
import InvestorShortInfo from '../../investor-display/InvestorShortInfo';
import {
  asNumber,
  asString,
  buildInvestorProfilePath,
  getInvestorTypes,
  hasAnyInvestorAccessField,
} from '../../investor-display/helpers';

// lazy: CTA is shown only once per page
const CTA = lazy(() => import('../../ui/CTA'));

// ——— tiny UI ———
const Shimmer = memo(({ className = '' }) => (
  <div className={`bg-stone-200 rounded animate-pulse ${className}`} />
));

// ——— skeleton card (pixel-perfect match to the real card) ———
const SkeletonCard = memo(() => (
  <div className="bg-white border border-stone-300 shadow-md shadow-blue-50 rounded-md p-6 flex flex-col h-full">
    {/* Header */}
    <div className="flex items-start mb-4 gap-2">
      <div className="flex items-start gap-2 min-w-0 flex-1">
        {/* InvestorLogo 24x24 */}
        <Shimmer className="w-6 h-6 rounded" />
        {/* Name (link) */}
        <div className="flex flex-col">
          <Shimmer className="h-5 w-48 rounded" />
        </div>
      </div>
      {/* 24x16 flag */}
      <Shimmer className="w-6 h-4 rounded" />
    </div>

    {/* Icons + deals on the right */}
    <div className="flex items-center justify-between mb-4">
      {/* Left icons in pills 20x20 */}
      <div className="flex items-center space-x-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <span key={i} className="inline-flex items-center justify-center w-5 h-5 align-middle">
            <Shimmer className="w-5 h-5 rounded" />
          </span>
        ))}
      </div>

      {/* Right block: handshake + underlined text */}
      <div className="flex items-center gap-1.5">
        <Shimmer className="h-3.5 w-3.5 rounded" />
        <Shimmer className="h-4 w-28 rounded" />
      </div>
    </div>

    {/* Types */}
    <div className="flex flex-wrap gap-2 mb-4">
      <Shimmer className="h-5 w-16 rounded" />
      <Shimmer className="h-5 w-12 rounded" />
      <Shimmer className="h-5 w-20 rounded" />
    </div>

    {/* Industries */}
    <div className="flex flex-wrap gap-2 mb-4">
      <Shimmer className="h-6 w-20 rounded-full" />
      <Shimmer className="h-6 w-24 rounded-full" />
      <Shimmer className="h-6 w-16 rounded-full" />
      {/* moreIndustries button placeholder */}
      <Shimmer className="h-6 w-24 rounded-full" />
    </div>

    {/* Short info */}
    <div className="space-y-2">
      <Shimmer className="h-3 w-full rounded" />
      <Shimmer className="h-3 w-5/6 rounded" />
      <Shimmer className="h-3 w-2/3 rounded" />
    </div>
  </div>
));

// ——— memoized card ———
const InvestorsCard = memo(function InvestorsCard({ inv, idx, t, linkOf }) {
  const deals = asNumber(inv?.investment_stats?.deals_total);
  const hasAccessIcons = useMemo(() => hasAnyInvestorAccessField(inv), [inv]);
  const investorTypes = useMemo(() => getInvestorTypes(inv), [inv]);

  const dealsText = useMemo(
    () => (deals > 0 ? `${deals.toLocaleString()} ${t('pageInvProf.records1')}` : ''),
    [deals, t]
  );

  const nameText = asString(inv?.name) || '—';

  return (
    <article className="bg-white border border-stone-300 shadow-md shadow-blue-50 rounded-md p-6 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start mb-4 gap-2">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <InvestorLogo
            id={inv?.id}
            country={inv?.country}
            className="flex-none w-6 h-6"
            alt={`${nameText} logo`}
            width={24}
            height={24}
            loading="lazy"
            decoding="async"
          />
          <h3 className="leading-tight">
            <Link
              to={linkOf(inv, idx)}
              className="text-lg font-bold text-gray-900 hover:underline whitespace-normal break-words inline-block align-middle"
            >
              <span className="inline">{nameText}</span>
              <ExternalLink size={14} className="inline ml-1 align-baseline" />
            </Link>
          </h3>
        </div>
        <div className="w-6 h-4 flex-none overflow-hidden">
          <CountryFlag
            countryName={asString(inv?.country)}
            width={24}
            height={16}
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      {/* Icons + deals on the right */}
      {(hasAccessIcons || deals > 0) && (
        <nav className="flex items-center justify-between mb-4" aria-label="Social and contacts">
          <InvestorAccessIcons investor={inv} socialMode="hint" />
          {deals > 0 && (
            <div className="flex items-center gap-1.5">
              <Handshake size={14} className="text-stone-950" aria-hidden="true" />
              <a
                href={`${linkOf(inv, idx)}#last-investments`}
                className="inline-flex underline underline-offset-4 decoration-blue-600 decoration-1 text-sm"
                aria-label={t('investorCard.viewRecentDeals')}
              >
                {dealsText}
              </a>
            </div>
          )}
        </nav>
      )}

      {/* Types */}
      <div className="flex flex-wrap gap-2 mb-4 ">
        {investorTypes.map((tpe, i) => (
          <span key={`${tpe}-${i}`} className="text-sm">{tpe.trim()}</span>
        ))}
      </div>

      {/* Industries */}
      <InvestorIndustries industries={inv?.industries} t={t} />

      {/* Short info */}
      <InvestorShortInfo shortInfo={inv?.short_info} t={t} />
    </article>
  );
});

// ——— main ———
const InvestorsListCards = ({
  currentInvestors = [],
  startIndex = 0, // kept for compatibility
  isLoading = false,
  pageSize = 10,
  lng,
}) => {
  const { t, i18n } = useTranslation();

  // stable link builder
  const linkOf = useMemo(() => {
    return (inv, idx = 0) => {
      return buildInvestorProfilePath({
        investor: inv,
        language: i18n.language,
        trailingSlash: true,
        fallbackId: String(idx),
      });
    };
  }, [i18n.language]);

  // items: either skeletons or cards
  const items = useMemo(
    () => (isLoading ? Array.from({ length: pageSize }) : currentInvestors),
    [isLoading, currentInvestors, pageSize]
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6 pr-0 sm:pr-6">
      {items.map((inv, idx) => {
        const stableKey =
          (inv && (inv.id ?? inv.uid ?? inv.slug)) || `skeleton-${idx}`;
        return (
          <Fragment key={stableKey}>
            {!isLoading && idx === 4 && (
              <div className="col-span-1 sm:col-span-2">
                <Suspense fallback={<Shimmer className="w-full h-24" />}>
                  <CTA lng={i18n.language || lng} backgroundPosition="50% 40%" />
                </Suspense>
              </div>
            )}

            <div className="col-span-1">
              {isLoading ? (
                <SkeletonCard />
              ) : (
                <InvestorsCard inv={inv} idx={idx} t={t} linkOf={linkOf} />
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};

export default InvestorsListCards;
